import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, PackagePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';

import {
  Button,
  ConfirmDialog,
  Form,
  SideDrawer,
  StepperAccordion,
  StepperAccordionSummary,
  useStepperAccordion,
} from '@klareospace/pharos-lib-web-utils';
import type {
  StepperAccordionLabels,
  StepperAccordionSection,
  StepperAccordionSummaryItem,
  UseStepperAccordionReturn,
} from '@klareospace/pharos-lib-web-utils';
import { formatPhoneNumber } from '@klareospace/pharos-lib-front-utils';
import type { Customer } from '@klareospace/pharos-lib-types';
import { assertNever, isDefined } from '@klareospace/pharos-lib-utils';
import type { DateRangeValue } from '@klareospace/pharos-lib-web-utils';

import { getCustomerDisplayName } from '&/features/customers';

import {
  CONTENT_SECTION_ID,
  CUSTOMER_SECTION_ID,
  GENERAL_INFORMATION_SECTION_ID,
  ORDER_CREATION_SECTION_IDS,
  ROUTE_INFORMATION_SECTION_ID,
  SCHEDULING_SECTION_ID,
} from '../constants';
import type { OrderCreationSectionId } from '../constants';
import { useOrderCreationForm, useShipperOptions, useSubmitOrder } from '../hooks';
import type { ShipperOptionsStatus } from '../hooks';
import type { RouteInformationFormInput } from '../schemas/order-creation.schema';
import {
  buildDefaultTimeSlotValue,
  buildDeliveryAddressEntry,
  isCrossLegTimeWindowInverted,
  isPickupTimeSlotInverted,
  isRouteInformationComplete,
  isSchedulingComplete,
  isSchedulingTimeSlotInverted,
  resolveRouteInformationErrorMessageKey,
  resolveSchedulingSeedDecision,
} from '../utils';
import { ContentSectionSummary } from './content-section-summary';
import { RouteInformationSectionSummary } from './route-information-section-summary';
import { SchedulingSectionSummary } from './scheduling-section-summary';
import {
  ContentSection,
  CustomerSection,
  GeneralInformationSection,
  RouteInformationSection,
  SchedulingSection,
} from './sections';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderCreationDrawerProps {
  onClose: () => void;
  open: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

// General information, Customer, Route information, and Scheduling are built in-render: they need
// live, fetched state (`orderTypes`/`sites`) or live form state (`selectedCustomer`/`selectedSite`)
// passed down as props. Content takes no props, so it stays static — it owns its live state
// internally via its own hooks instead of receiving it from the drawer.
type StaticSectionId = Exclude<
  OrderCreationSectionId,
  'customer' | 'generalInformation' | 'routeInformation' | 'scheduling'
>;

const SECTION_CONTENT: Record<StaticSectionId, React.ReactNode> = {
  content: <ContentSection />,
};

/** Statuses for which the Shipper field in General information blocks submission entirely (the full explanatory message lives there — not duplicated in the footer). */
const SHIPPER_BLOCKED_STATUSES: ShipperOptionsStatus[] = ['error', 'forbidden', 'none'];

/** DOM id of the footer hint shown next to a disabled "Create delivery" button, wired via `aria-describedby`. */
const SHIPPER_MESSAGE_ID = 'order-creation-shipper-message';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Manual order creation drawer — renders the numbered `StepperAccordion` sections with Next/Back
 * navigation. General information starts closed and pre-validated (pre-filled with the systemic
 * Cart Drop Off order type) with Customer active. Closing (X / Esc) always confirms, since
 * outside-click dismissal is disabled entirely for this data-entry drawer.
 */
export function OrderCreationDrawer({ onClose, open }: OrderCreationDrawerProps): React.JSX.Element {
  const { t } = useTranslation('order-creation');
  const stepper = useStepperAccordion([...ORDER_CREATION_SECTION_IDS], {
    initiallyCompletedSectionIds: [GENERAL_INFORMATION_SECTION_ID],
  });
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const { options: shipperOptions, status: shipperOptionsStatus } = useShipperOptions(open);
  const {
    canListSites,
    form,
    isSyncingAddressBook,
    orderTypes,
    sites,
    syncAddressBook,
    validateContent,
    validateCustomer,
    validateGeneralInformation,
    validateRouteInformation,
  } = useOrderCreationForm({ open, shipperOptions });
  const generalInformation = useWatch({ control: form.control, name: 'generalInformation' });
  const selectedCustomer = useWatch({ control: form.control, name: 'customer.selectedCustomer' });
  const selectedSite = useWatch({ control: form.control, name: 'routeInformation.selectedSite' });

  const allSectionsComplete = ORDER_CREATION_SECTION_IDS.every((id) => stepper.completedSectionIds.includes(id));
  const { canSubmit, handleSubmit, isSubmitting } = useSubmitOrder({
    allSectionsComplete,
    form,
    onSuccess: resetAndClose,
    orderTypes,
    shipperOptionsStatus,
    uncompleteSection: stepper.uncompleteSection,
    validateContent,
    validateCustomer,
    validateGeneralInformation,
    validateRouteInformation,
  });
  const isShipperUnavailable = SHIPPER_BLOCKED_STATUSES.includes(shipperOptionsStatus);

  // `stepper` is a fresh object every render (its functions aren't memoized), so the watch
  // subscription below reads it through this ref instead of depending on `stepper` directly —
  // that keeps the subscription itself created exactly once per mount. Assigned directly during
  // render (not inside a `useEffect`) so it is never stale: a `useEffect`-based sync would still
  // run in hook-declaration order, which can fire a cascading `form.watch` callback (e.g. from
  // another effect inside `useOrderCreationForm`, triggered by the same state update) before this
  // one has flushed, reading the previous render's stepper instead of the one just computed.
  const stepperRef = useRef<UseStepperAccordionReturn>(stepper);
  stepperRef.current = stepper;

  // Shipper-driven first section: with more than one shipper in scope, General information must
  // start open and not completed so the user is forced to pick one — a single ready shipper already
  // pre-selects via defaults and keeps the pre-completed behavior untouched, as do the
  // loading/none/forbidden/error statuses. One-shot per drawer open via a ref guard, reset when
  // `open` flips false, so a later options refetch or the user clearing the shipper field never
  // re-triggers it.
  const shipperDrivenUncompleteRef = useRef(false);

  useEffect(() => {
    if (!open) {
      shipperDrivenUncompleteRef.current = false;
      return;
    }
    if (shipperDrivenUncompleteRef.current) return;
    if (shipperOptionsStatus !== 'ready' || shipperOptions.length <= 1) return;
    if (form.getValues('generalInformation.shipperCode')) return;
    shipperDrivenUncompleteRef.current = true;
    stepperRef.current.uncompleteSection(GENERAL_INFORMATION_SECTION_ID);
  }, [form, open, shipperOptions.length, shipperOptionsStatus]);

  // Auto-completion: once pickup site + delivery address (+ a valid-or-disabled timeslot) resolve,
  // mark Route information complete without requiring the user to visit it — clearing any stale
  // section-level error — and un-complete it again (setting the timeslot-specific message) if an
  // already-auto-completed section later becomes incomplete (e.g. the timeslot is edited into an
  // invalid range). Subscribes narrowly via `form.watch` (instead of a top-level
  // `useWatch('routeInformation')`) so a Route information keystroke does not re-render the whole
  // drawer. RHF's own `form.reset()` fires this watch callback with `name === undefined` — that
  // event is ignored, otherwise `handleConfirmClose` closing with Route information auto-completed
  // would run this evaluation against the just-reset form and re-activate it after `stepper.reset()`
  // already set Customer active.
  useEffect(() => {
    function evaluate(route: RouteInformationFormInput): void {
      const { completedSectionIds, completeSection, uncompleteSection } = stepperRef.current;
      if (isRouteInformationComplete(route)) {
        completeSection(ROUTE_INFORMATION_SECTION_ID);
        form.clearErrors('routeInformation');
        return;
      }
      if (completedSectionIds.includes(ROUTE_INFORMATION_SECTION_ID)) {
        uncompleteSection(ROUTE_INFORMATION_SECTION_ID);
      }
      if (route.selectedSite && route.deliveryAddress && isPickupTimeSlotInverted(route.pickupTimeSlot)) {
        form.setError('routeInformation', {
          message: t(resolveRouteInformationErrorMessageKey(route)),
          type: 'manual',
        });
      }
    }

    evaluate(form.getValues('routeInformation'));
    const { unsubscribe } = form.watch((_values, { name }) => {
      if (!isDefined(name)) return;
      if (!name.startsWith(ROUTE_INFORMATION_SECTION_ID)) return;
      evaluate(form.getValues('routeInformation'));
    });
    return unsubscribe;
    // Deliberately `[form]` only (not `stepper`) — subscribes once per mount by design (see
    // comment above); `form` is a stable RHF instance for the component's lifetime.
  }, [form]);

  // Seeds the Scheduling delivery timeslot once the section first becomes active (so "next
  // available slot" is computed on arrival, not at drawer open) and re-seeds it — resetting the
  // carrier to "Not assigned" alongside it — whenever the pickup site changes while the timeslot is
  // still the one last auto-seeded, regardless of which section is active (a collapsed, already-
  // completed Scheduling section must never keep showing a stale site's slot/carrier until
  // reopened). `resolveSchedulingSeedDecision` (pure, unit-tested) owns the decision; reference
  // equality against `lastSeededTimeSlotRef` is what detects a manual edit, since every
  // DateRangeFilter onChange produces a brand-new value object — so the ref is seeded from
  // `form.getValues()` right after `setValue`, not from the local `seeded` object, since RHF does
  // not guarantee that what it later returns from `getValues` is reference-equal to what was passed
  // into `setValue`.
  const lastSeededSchedulingSiteIdRef = useRef<string | null>(null);
  const lastSeededSchedulingTimeSlotRef = useRef<DateRangeValue | null>(null);

  useEffect(() => {
    if (!open) return;
    const currentTimeSlot = form.getValues('scheduling.deliveryTimeSlot');
    const selectedSiteId = selectedSite?.id ?? null;
    const decision = resolveSchedulingSeedDecision({
      currentTimeSlot,
      isSchedulingActive: stepper.activeSectionId === SCHEDULING_SECTION_ID,
      lastSeededSiteId: lastSeededSchedulingSiteIdRef.current,
      lastSeededTimeSlot: lastSeededSchedulingTimeSlotRef.current,
      selectedSiteId,
    });
    if (!decision.seed) {
      lastSeededSchedulingSiteIdRef.current = selectedSiteId;
      return;
    }
    form.setValue('scheduling.deliveryTimeSlot', buildDefaultTimeSlotValue(selectedSite, 'today'), {
      shouldValidate: true,
    });
    if (decision.resetCarrier) form.setValue('scheduling.carrierCode', '', { shouldValidate: true });
    lastSeededSchedulingSiteIdRef.current = selectedSiteId;
    lastSeededSchedulingTimeSlotRef.current = form.getValues('scheduling.deliveryTimeSlot');
  }, [form, open, selectedSite, stepper.activeSectionId]);

  // Auto-completion, mirroring Route information above: mark Scheduling complete once a valid
  // delivery timeslot is set and the cross-leg constraint holds (delivery must not end before the
  // pickup timeslot starts), clearing any stale error — and un-complete it again, setting the
  // matching message, if an already-auto-completed section later becomes incomplete. Also watches
  // `routeInformation.pickupTimeSlot` (not just `scheduling.*`) so fixing the cross-leg violation
  // from the pickup side (Route information) re-completes Scheduling instead of staying deadlocked.
  useEffect(() => {
    function evaluate(): void {
      const { completedSectionIds, completeSection, uncompleteSection } = stepperRef.current;
      const scheduling = form.getValues('scheduling');
      const pickupTimeSlot = form.getValues('routeInformation.pickupTimeSlot');
      const crossLegViolated = isCrossLegTimeWindowInverted(pickupTimeSlot, scheduling.deliveryTimeSlot);

      if (isSchedulingComplete(scheduling) && !crossLegViolated) {
        completeSection(SCHEDULING_SECTION_ID);
        form.clearErrors('scheduling');
        return;
      }
      if (completedSectionIds.includes(SCHEDULING_SECTION_ID)) {
        uncompleteSection(SCHEDULING_SECTION_ID);
      }
      if (isSchedulingTimeSlotInverted(scheduling)) {
        form.setError('scheduling', { message: t('sections.scheduling.invalidRange'), type: 'manual' });
      } else if (crossLegViolated) {
        form.setError('scheduling', { message: t('submit.crossLegTimeWindow'), type: 'manual' });
      }
    }

    evaluate();
    const { unsubscribe } = form.watch((_values, { name }) => {
      if (!isDefined(name)) return;
      if (!name.startsWith(SCHEDULING_SECTION_ID) && !name.startsWith('routeInformation.pickupTimeSlot')) return;
      evaluate();
    });
    return unsubscribe;
    // Deliberately `[form]` only — same rationale as the Route information effect above.
  }, [form]);

  const labels: StepperAccordionLabels = {
    completed: t('accordion.badge.completed'),
    locked: t('accordion.badge.locked'),
    stepPosition: (position, total): string => t('accordion.badge.stepPosition', { position, total }),
  };

  const referenceValue =
    generalInformation.referenceNumber.trim() || t('sections.generalInformation.fields.reference.autoGenerated');
  const orderTypeLabel = orderTypes.find((type) => type.id === generalInformation.orderTypeId)?.label ?? '';
  const shipperLabel = shipperOptions.find((option) => option.code === generalInformation.shipperCode)?.label ?? '';

  // Predicts whether the upcoming pre-fill completes Route information; if so, skip straight to
  // Content instead of landing on an already-complete section. Explicit ids rather than a second
  // `goNext()`, which would read the stale active section.
  function handleCustomerSelect(customer: Customer): void {
    form.setValue('customer.selectedCustomer', customer, { shouldValidate: true });

    const predictedRoute: RouteInformationFormInput = {
      ...form.getValues('routeInformation'),
      deliveryAddress: buildDeliveryAddressEntry(customer),
    };
    if (!isRouteInformationComplete(predictedRoute)) {
      stepper.goNext();
      return;
    }
    stepper.completeSection(CUSTOMER_SECTION_ID);
    stepper.completeSection(ROUTE_INFORMATION_SECTION_ID);
    stepper.activateSection(CONTENT_SECTION_ID);
  }

  function handleCustomerRemove(): void {
    form.setValue('customer.selectedCustomer', null, { shouldValidate: false });
    form.clearErrors('customer.selectedCustomer');
    // Route information first so the final active section (set by the last call) is Customer —
    // its own delivery address was cleared along with the customer and is no longer complete.
    stepper.uncompleteSection(ROUTE_INFORMATION_SECTION_ID);
    stepper.uncompleteSection(CUSTOMER_SECTION_ID);
  }

  function resolveSectionContent(id: OrderCreationSectionId): React.ReactNode {
    if (id === GENERAL_INFORMATION_SECTION_ID) {
      return (
        <GeneralInformationSection
          orderTypes={orderTypes}
          shipperOptions={shipperOptions}
          shipperStatus={shipperOptionsStatus}
        />
      );
    }
    if (id === CUSTOMER_SECTION_ID) {
      return (
        <CustomerSection
          onRemove={handleCustomerRemove}
          onSelect={handleCustomerSelect}
          selectedCustomer={selectedCustomer}
        />
      );
    }
    if (id === ROUTE_INFORMATION_SECTION_ID) {
      return (
        <RouteInformationSection
          canListSites={canListSites}
          isSyncingAddressBook={isSyncingAddressBook}
          onSyncAddressBook={syncAddressBook}
          sites={sites}
        />
      );
    }
    if (id === SCHEDULING_SECTION_ID) return <SchedulingSection selectedSite={selectedSite} />;
    return SECTION_CONTENT[id];
  }

  // Name is always shown; Email and Phone are only included when they resolve to a non-empty
  // value, so no "Label:" with a blank value ever appears in the closed-section summary.
  function buildCustomerSummaryItems(customer: Customer): StepperAccordionSummaryItem[] {
    const email = customer.email ?? '';
    const phone = formatPhoneNumber(customer.phone);

    const items: StepperAccordionSummaryItem[] = [
      { label: t('sections.customer.fields.name'), value: getCustomerDisplayName(customer) },
    ];
    if (email) items.push({ label: t('sections.customer.fields.email'), value: email });
    if (phone) items.push({ label: t('sections.customer.fields.phone'), value: phone });
    return items;
  }

  // Name/Order type are always shown; Shipper is only included once resolved (e.g. not yet while
  // the options are still loading), mirroring the Customer summary's "no label with a blank value" rule.
  function buildGeneralInformationSummaryItems(): StepperAccordionSummaryItem[] {
    const items: StepperAccordionSummaryItem[] = [
      { label: t('sections.generalInformation.fields.reference.label'), value: referenceValue },
      { label: t('sections.generalInformation.fields.orderType.label'), value: orderTypeLabel },
    ];
    if (shipperLabel) items.push({ label: t('sections.generalInformation.fields.shipper.label'), value: shipperLabel });
    return items;
  }

  function resolveSectionSummary(id: OrderCreationSectionId): React.ReactNode {
    if (id === GENERAL_INFORMATION_SECTION_ID) {
      return <StepperAccordionSummary items={buildGeneralInformationSummaryItems()} />;
    }
    if (id === CUSTOMER_SECTION_ID) {
      return selectedCustomer ? (
        <StepperAccordionSummary items={buildCustomerSummaryItems(selectedCustomer)} />
      ) : undefined;
    }
    if (id === CONTENT_SECTION_ID) return <ContentSectionSummary />;
    if (id === ROUTE_INFORMATION_SECTION_ID) return <RouteInformationSectionSummary />;
    if (id === SCHEDULING_SECTION_ID) return <SchedulingSectionSummary />;
    return assertNever(id);
  }

  const sections: StepperAccordionSection[] = ORDER_CREATION_SECTION_IDS.map((id) => ({
    content: resolveSectionContent(id),
    id,
    summary: resolveSectionSummary(id),
    title: t(`sections.${id}.title`),
  }));

  // Keyed by `string` (not `OrderCreationSectionId`) since `stepper.activeSectionId` is a generic
  // `string | null` from `useStepperAccordion` — the keys themselves are still written from the
  // typed section id constants, so no cast is needed to look up by the active section id.
  const sectionValidators: Partial<Record<string, () => Promise<boolean>>> = {
    [CONTENT_SECTION_ID]: validateContent,
    [CUSTOMER_SECTION_ID]: validateCustomer,
    [GENERAL_INFORMATION_SECTION_ID]: validateGeneralInformation,
    [ROUTE_INFORMATION_SECTION_ID]: validateRouteInformation,
  };

  async function handleGoNext(): Promise<void> {
    const validate = stepper.activeSectionId ? sectionValidators[stepper.activeSectionId] : undefined;
    if (validate) {
      const canProceed = await validate();
      if (!canProceed) return;
    }
    stepper.goNext();
  }

  /** A submission in flight must never be interrupted by a close attempt (X / Esc) — it is a silent no-op until the submission settles. */
  function handleOpenChange(nextOpen: boolean): void {
    if (nextOpen || isSubmitting) return;
    setIsCloseConfirmOpen(true);
  }

  /** Shared by the discard-close confirmation and a successful submission — both fully reset the drawer, including dismissing a still-open close confirmation. */
  function resetAndClose(): void {
    setIsCloseConfirmOpen(false);
    stepper.reset();
    form.reset();
    onClose();
  }

  function handleConfirmClose(): void {
    resetAndClose();
  }

  return (
    <>
      <SideDrawer
        dismissOnOutsideClick={false}
        footer={
          <div className="flex w-full items-center justify-between">
            <Button disabled={!stepper.canGoBack} onClick={stepper.goBack} type="button" variant="outline">
              <ArrowLeft aria-hidden="true" className="h-4 w-4 rtl:rotate-180" />
              {t('nav.back')}
            </Button>
            {stepper.isLastSection ? (
              <div className="flex items-center gap-3">
                {isShipperUnavailable && (
                  <p className="text-sm text-destructive" id={SHIPPER_MESSAGE_ID}>
                    {t('submit.shipperUnavailable')}
                  </p>
                )}
                <Button
                  aria-describedby={isShipperUnavailable ? SHIPPER_MESSAGE_ID : undefined}
                  disabled={!canSubmit}
                  onClick={(): void => void handleSubmit()}
                  type="button"
                >
                  {t('nav.createDelivery')}
                </Button>
              </div>
            ) : (
              <Button onClick={(): void => void handleGoNext()} type="button">
                {t('nav.next')}
              </Button>
            )}
          </div>
        }
        headerContent={
          <div className="flex items-center gap-2 pe-8">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
              <PackagePlus aria-hidden="true" className="h-4 w-4" />
            </div>
            <span className="text-lg font-semibold text-foreground">{t('title')}</span>
          </div>
        }
        headerProgress={{
          ariaLabel: t('header.progressAriaLabel'),
          max: ORDER_CREATION_SECTION_IDS.length,
          value: stepper.completedSectionIds.length,
        }}
        onOpenChange={handleOpenChange}
        open={open}
        title={t('title')}
        widthClassName="w-full sm:min-w-[75vw] sm:max-w-[90vw] xl:min-w-[50vw] xl:max-w-[50vw]"
      >
        <Form {...form}>
          <StepperAccordion
            activeSectionId={stepper.activeSectionId}
            ariaLabel={t('accordion.ariaLabel')}
            completedSectionIds={stepper.completedSectionIds}
            labels={labels}
            onActiveSectionChange={stepper.openSection}
            sections={sections}
            unlockedSectionIds={stepper.unlockedSectionIds}
          />
        </Form>
      </SideDrawer>
      <ConfirmDialog
        cancelLabel={t('closeConfirm.cancel')}
        confirmLabel={t('closeConfirm.confirm')}
        description={t('closeConfirm.description')}
        onConfirm={handleConfirmClose}
        onOpenChange={setIsCloseConfirmOpen}
        open={isCloseConfirmOpen}
        title={t('closeConfirm.title')}
      />
    </>
  );
}
